import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, send, update, voidRecord} from '../services/InvoicesService';
import {Invoice} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {invoicesKeys} from './useInvoicesQuery';

/**
 * FNP-918 (§6): build the mutation payload by WHITELISTING the fields the API's
 * Input type declares.
 *
 * It was previously built by spreading the record and `delete`-ing `created`
 * and `updated`. Two problems with that shape:
 *
 *   1. It is a DENYLIST. Every field the UI adds to the record — the derived
 *      `createdAtMillis` added by useInvoices, for one — is silently sent to
 *      the API until someone remembers to `delete` it too. A whitelist cannot
 *      leak a field nobody thought about.
 *   2. The same pattern one layer up (`delete record.created` on the caller's
 *      object) MUTATES the live Formik/React Query object. `applications`
 *      shipped exactly that for months (FNP-996).
 *
 * `createdAtMillis` is the concrete case here: a client-side derivation the
 * API's Input type would reject.
 *
 * The fields below are the ones the form actually collects (FormComponent:
 * customer, currency, collection method, days until due) plus `description`.
 * Server-owned lifecycle values — status, paid, amounts, number, the period
 * bounds — are deliberately NOT sent: Stripe computes them, and echoing a
 * stale copy back is how a UI overwrites a value it never owned.
 */
const toInvoiceInput = (values: Invoice) => ({
    customerId: values.customerId,
    currency: values.currency,
    description: values.description,
    collectionMethod: values.collectionMethod,
    daysUntilDue: values.daysUntilDue,
});

export const useInvoice = (invoiceId?: string) => {
    const queryClient = useQueryClient();

    const invoiceQuery = useQuery({
        queryKey: invoicesKeys.detail(invoiceId ?? ''),
        queryFn: () => fetch(invoiceId!),
        enabled: !!invoiceId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Invoice) => add(toInvoiceInput(values)),
        onSuccess: () => {
            notifySuccess('Invoice created');
            queryClient.invalidateQueries({queryKey: invoicesKeys.all});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Invoice) => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return update(invoiceId, {
                id: invoiceId,
                ...toInvoiceInput(values),
            } as Invoice);
        },
        onSuccess: () => {
            notifySuccess('Invoice updated');
            // Detail-page mutations invalidate BOTH the detail key and the
            // list key, or the list keeps showing the pre-edit values (§8).
            queryClient.invalidateQueries({queryKey: invoicesKeys.all});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return deleteRecord(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice deleted');
            queryClient.invalidateQueries({queryKey: invoicesKeys.all});
        },
        onError: notifyError,
    });

    const sendMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return send(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice sent');
            queryClient.invalidateQueries({queryKey: invoicesKeys.all});
        },
        onError: notifyError,
    });

    const voidMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return voidRecord(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice voided');
            queryClient.invalidateQueries({queryKey: invoicesKeys.all});
        },
        onError: notifyError,
    });

    return {
        record: invoiceQuery.data,
        loading: invoiceQuery.isLoading,
        fetchRecord: invoiceQuery.refetch,
        // ⚠️ mutateAsync, NOT mutate (§6/§7).
        //
        // `mutate` is fire-and-forget: it returns void, so the drawer's
        // `await handleSubmit(values); onClose();` awaits `undefined` and
        // closes IMMEDIATELY. A server-side rejection then destroys the
        // operator's input and toasts into a closed drawer, and Formik clears
        // `isSubmitting` on the same tick so the submit button's `disabled`
        // never engages. `mutateAsync` returns the real promise, which makes
        // both the drawer's await and the disabled-while-submitting wiring do
        // what they already claim to do.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        handleSend: sendMutation.mutateAsync,
        handleVoid: voidMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
        sendMutation,
        voidMutation,
    };
};
